import React from 'react';
import { motion } from 'framer-motion';
import {
  ScenarioData,
  calcListingCompensation,
  calcBuyerAgentCompensation,
  calcRevenueStamps,
  calcTotalExpenses,
  calcNetProceeds,
  formatCurrency,
} from '@/lib/index';
import { CurrencyInput, PercentInput } from './Inputs';
import { DatePickerInput } from './DatePickerInput';
import { TrendingUp, TrendingDown, Link2 } from 'lucide-react';

interface ScenarioCardProps {
  index: 0 | 1 | 2;
  scenario: ScenarioData;
  onChange: (field: keyof ScenarioData, value: number | string) => void;
  isMirroredFrom1?: boolean;
}

// High-contrast section header — dark navy bg, white bold text, easy to read
function SectionHeader({ title }: { title: string }) {
  return (
    <tr>
      <td
        colSpan={2}
        className="bg-primary text-primary-foreground text-sm font-bold uppercase tracking-wide px-4 py-2.5"
      >
        {title}
      </td>
    </tr>
  );
}

function Row({
  label,
  children,
  dimLabel,
  isTotal,
}: {
  label: string;
  children: React.ReactNode;
  dimLabel?: boolean;
  isTotal?: boolean;
}) {
  return (
    <tr className={`border-b border-border/50 hover:bg-muted/30 transition-colors ${isTotal ? 'bg-secondary/60' : ''}`}>
      <td
        className={`py-2.5 px-4 text-sm leading-snug pr-3 w-[55%] ${
          isTotal
            ? 'font-bold text-foreground uppercase tracking-wide text-xs'
            : dimLabel
            ? 'text-muted-foreground'
            : 'text-foreground font-medium'
        }`}
      >
        {label}
      </td>
      <td className="py-2 px-3 w-[45%]">{children}</td>
    </tr>
  );
}

const SCENARIO_COLORS = ['#1e3a6e', '#1e3a6e', '#1e3a6e'];
const SCENARIO_LABELS = ['Scenario 1', 'Scenario 2', 'Scenario 3'];

export function ScenarioCard({ index, scenario, onChange, isMirroredFrom1 = false }: ScenarioCardProps) {
  const listingComp = calcListingCompensation(scenario.salesPrice, scenario.listingCommissionPct);
  const buyerComp = calcBuyerAgentCompensation(scenario.salesPrice, scenario.buyerAgentCommissionPct);
  const revenueStamps = calcRevenueStamps(scenario.salesPrice);
  const totalExpenses = calcTotalExpenses(scenario);
  const netProceeds = calcNetProceeds(scenario);
  const isPositive = netProceeds >= 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: index * 0.06 }}
      className="flex flex-col rounded-xl border border-border overflow-hidden bg-card"
      style={{ boxShadow: '0 2px 16px -2px rgba(30,58,110,0.10)' }}
    >
      {/* ── Card Header ─────────────────────────────── */}
      <div className="px-4 py-4 text-white" style={{ background: SCENARIO_COLORS[index] }}>
        <div className="flex items-center justify-between mb-1">
          <p className="text-sm font-bold uppercase tracking-widest opacity-80">{SCENARIO_LABELS[index]}</p>
          {isMirroredFrom1 && (
            <span className="inline-flex items-center gap-1 text-xs bg-white/20 px-2.5 py-1 rounded-full font-medium">
              <Link2 size={10} />
              Synced from S1
            </span>
          )}
        </div>
        <p className="font-mono text-2xl font-bold">{formatCurrency(scenario.salesPrice)}</p>
      </div>

      {/* ── Net Proceeds Hero ───────────────────────── */}
      <div
        className={`px-4 py-3 flex items-center justify-between border-b border-border ${
          isPositive ? 'bg-emerald-50' : 'bg-red-50'
        }`}
      >
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Est. Net Proceeds</p>
          <p className={`font-mono text-xl font-bold mt-0.5 ${isPositive ? 'text-emerald-700' : 'text-red-600'}`}>
            {formatCurrency(netProceeds)}
          </p>
        </div>
        {isPositive ? (
          <TrendingUp size={26} className="text-emerald-500" />
        ) : (
          <TrendingDown size={26} className="text-red-400" />
        )}
      </div>

      {/* ── Table Body ──────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        <table className="w-full border-collapse">
          <tbody>

            {/* SALES PRICE */}
            <SectionHeader title="Sales Price" />
            <Row label="Sales Price">
              <CurrencyInput value={scenario.salesPrice} onChange={v => onChange('salesPrice', v as number)} />
            </Row>
            <Row label="Settlement Date">
              <DatePickerInput
                value={scenario.settlementDate}
                onChange={v => onChange('settlementDate', v)}
                placeholder="Select date"
              />
            </Row>

            {/* PRE-MARKETING */}
            <SectionHeader title="Pre-Marketing Expenses" />
            <Row label="Appraisal">
              <CurrencyInput value={scenario.appraisal} onChange={v => onChange('appraisal', v as number)} />
            </Row>
            <Row label="Inspections">
              <CurrencyInput value={scenario.inspections} onChange={v => onChange('inspections', v as number)} />
            </Row>
            <Row label="Repairs / Upfits">
              <CurrencyInput value={scenario.repairsUpfits} onChange={v => onChange('repairsUpfits', v as number)} />
            </Row>
            <Row label="Other" dimLabel>
              <CurrencyInput value={scenario.preMarketingOther1} onChange={v => onChange('preMarketingOther1', v as number)} />
            </Row>
            <Row label="Other" dimLabel>
              <CurrencyInput value={scenario.preMarketingOther2} onChange={v => onChange('preMarketingOther2', v as number)} />
            </Row>

            {/* MORTGAGES */}
            <SectionHeader title="Mortgages & Liens" />
            <Row label="1st Mortgage Payoff">
              <CurrencyInput value={scenario.mortgage1} onChange={v => onChange('mortgage1', v as number)} />
            </Row>
            <Row label="2nd Mortgage Payoff">
              <CurrencyInput value={scenario.mortgage2} onChange={v => onChange('mortgage2', v as number)} />
            </Row>
            <Row label="Home Equity Loan">
              <CurrencyInput value={scenario.homeEquityLoan} onChange={v => onChange('homeEquityLoan', v as number)} />
            </Row>
            <Row label="HOA Assessment(s)">
              <CurrencyInput value={scenario.hoaAssessment} onChange={v => onChange('hoaAssessment', v as number)} />
            </Row>
            <Row label="Fixture Liens">
              <CurrencyInput value={scenario.fixtureLiens} onChange={v => onChange('fixtureLiens', v as number)} />
            </Row>
            <Row label="Other Lien" dimLabel>
              <CurrencyInput value={scenario.otherLien1} onChange={v => onChange('otherLien1', v as number)} />
            </Row>
            <Row label="Other Lien" dimLabel>
              <CurrencyInput value={scenario.otherLien2} onChange={v => onChange('otherLien2', v as number)} />
            </Row>

            {/* CLOSING */}
            <SectionHeader title="Closing Expenses" />
            <Row label="Attorney Fee">
              <CurrencyInput value={scenario.attorneyFee} onChange={v => onChange('attorneyFee', v as number)} />
            </Row>
            <Row label="Listing Commission %">
              <PercentInput value={scenario.listingCommissionPct} onChange={v => onChange('listingCommissionPct', v)} />
            </Row>
            <Row label="Listing Compensation" dimLabel>
              <CurrencyInput value={listingComp} onChange={() => {}} readOnly highlight="calc" />
            </Row>
            <Row label="Compliance / Admin Fee">
              <CurrencyInput value={scenario.listingComplianceFee} onChange={v => onChange('listingComplianceFee', v as number)} />
            </Row>
            <Row label="Buyer Agent Commission %">
              <PercentInput value={scenario.buyerAgentCommissionPct} onChange={v => onChange('buyerAgentCommissionPct', v)} />
            </Row>
            <Row label="Buyer Agent Compensation" dimLabel>
              <CurrencyInput value={buyerComp} onChange={() => {}} readOnly highlight="calc" />
            </Row>
            <Row label="Seller-Paid Concessions">
              <CurrencyInput value={scenario.sellerConcessions} onChange={v => onChange('sellerConcessions', v as number)} />
            </Row>
            <Row label="Home Warranty">
              <CurrencyInput value={scenario.homeWarranty} onChange={v => onChange('homeWarranty', v as number)} />
            </Row>
            <Row label="Miscellaneous Fees">
              <CurrencyInput value={scenario.miscFees} onChange={v => onChange('miscFees', v as number)} />
            </Row>
            <Row label="Pro-Rated HOA Dues">
              <CurrencyInput value={scenario.proRatedHoaDues} onChange={v => onChange('proRatedHoaDues', v as number)} />
            </Row>
            <Row label="HOA Fees (cert. / transfer)">
              <CurrencyInput value={scenario.hoaFeesCertification} onChange={v => onChange('hoaFeesCertification', v as number)} />
            </Row>
            <Row label="Pro-Rated Taxes">
              <CurrencyInput value={scenario.proRatedTaxes} onChange={v => onChange('proRatedTaxes', v as number)} />
            </Row>
            <Row label="Recording Fees">
              <CurrencyInput value={scenario.recordingFees} onChange={v => onChange('recordingFees', v as number)} />
            </Row>
            <Row label="Repairs from Inspection">
              <CurrencyInput value={scenario.repairsFromInspection} onChange={v => onChange('repairsFromInspection', v as number)} />
            </Row>
            <Row label="Revenue Stamps ($1 / $500)" dimLabel>
              <CurrencyInput value={revenueStamps} onChange={() => {}} readOnly highlight="calc" />
            </Row>
            <Row label="Other" dimLabel>
              <CurrencyInput value={scenario.closingOther1} onChange={v => onChange('closingOther1', v as number)} />
            </Row>
            <Row label="Other" dimLabel>
              <CurrencyInput value={scenario.closingOther2} onChange={v => onChange('closingOther2', v as number)} />
            </Row>

            {/* TOTAL */}
            <Row label="Total Estimated Expenses" isTotal>
              <CurrencyInput value={totalExpenses} onChange={() => {}} readOnly highlight="total" />
            </Row>
            <Row label="Less Due Diligence Fee Received">
              <CurrencyInput value={scenario.dueDiligenceFeeReceived} onChange={v => onChange('dueDiligenceFeeReceived', v as number)} />
            </Row>

          </tbody>
        </table>
      </div>

      {/* ── Net Proceeds Footer ─────────────────────── */}
      <div className="px-4 py-4 border-t-2 border-border bg-primary">
        <p className="text-xs font-semibold text-primary-foreground/60 uppercase tracking-widest">
          Est. Net Proceeds to Seller
        </p>
        <p className={`font-mono text-2xl font-bold mt-1 ${isPositive ? 'text-accent' : 'text-red-300'}`}>
          {formatCurrency(netProceeds)}
        </p>
      </div>
    </motion.div>
  );
}
